// Cave84 — Open Bottle consumption flow (Phase 7)
// Spec §4.1 F1.3 + §4.9 Consumption & Tasting Notes.
// One batched write: bottle -> consumed, optional ConsumptionEvent (location),
// optional free-text TastingNote, optional WishlistEntry ("want again").
// Nothing is written until the caller confirms — see OpenBottleModal.

import {
  collection, doc, getDoc, getDocs, increment, limit, query,
  serverTimestamp, where, writeBatch
} from 'firebase/firestore';
import type { ConsumptionEvent, TastingNote, WishlistPriority } from '@cave84/shared-types';
import { db } from './firebase';
import { resolveWineIdForVintage } from './bottles';

// Firestore rejects `undefined` values — strip them before any write.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

export interface OpenBottleInput {
  bottleId: string;
  /** Required for bottle_count decrement on the parent Vintage + Wine. */
  vintageId: string;
  wineId?: string;
  location?: string;
  note?: string;
  addToWishlist?: boolean;
  wishlistPriority?: WishlistPriority;
}

/**
 * Marks a bottle consumed and, in the same batch, records where/what was
 * noted about the occasion. All writes commit atomically — either everything
 * lands, or nothing does.
 */
export async function completeOpenBottle(input: OpenBottleInput): Promise<void> {
  // Resolve wine_id up-front (single read) so counter decrements can join the batch.
  const wineId = input.wineId ?? (await resolveWineIdForVintage(input.vintageId));

  const batch = writeBatch(db);

  const bottleRef = doc(db, 'bottles', input.bottleId);
  batch.update(bottleRef, {
    status: 'consumed',
    is_special_occasion: false, // consumed bottles leave the Special Occasions list
    status_changed_date: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  // Keep denormalized bottle_count in sync — it was incremented on create.
  // Without this, stale-price crons keep queueing consumed-out vintages and
  // Investment valuations count bottles no longer owned.
  batch.update(doc(db, 'vintages', input.vintageId), { bottle_count: increment(-1) });
  if (wineId) {
    batch.update(doc(db, 'wines', wineId), { bottle_count: increment(-1) });
  }

  const note = input.note?.trim();
  let tastingNoteId: string | undefined;
  if (note) {
    const noteRef = doc(collection(db, 'tasting_notes'));
    tastingNoteId = noteRef.id;
    batch.set(noteRef, stripUndefined({
      bottle_id: input.bottleId,
      wine_id: wineId ?? undefined,
      date: serverTimestamp(),
      mode: 'free_text',
      free_text: note,
    }));
  }

  const location = input.location?.trim();
  if (location || tastingNoteId) {
    const eventRef = doc(collection(db, 'consumption_events'));
    batch.set(eventRef, stripUndefined({
      bottle_id: input.bottleId,
      date: serverTimestamp(),
      location,
      tasting_note_id: tastingNoteId,
    }));
  }

  if (input.addToWishlist && wineId) {
    const wishRef = doc(collection(db, 'wishlist'));
    batch.set(wishRef, stripUndefined({
      wine_id: wineId,
      priority: input.wishlistPriority ?? 'medium',
      status: 'active',
      date_added: serverTimestamp(),
      notes: 'Re-added from Open Bottle — want again.',
    }));
  }

  await batch.commit();
}

// ---------------------------------------------------------------------------
// Reads — surfaces the ConsumptionEvent + TastingNote back on Bottle Detail
// ---------------------------------------------------------------------------

export interface ConsumptionRecord {
  event: ConsumptionEvent | null;
  tastingNote: TastingNote | null;
}

/**
 * A bottle is consumed at most once (never reopened), so at most one
 * ConsumptionEvent exists per bottle_id.
 */
export async function getConsumptionForBottle(bottleId: string): Promise<ConsumptionRecord> {
  const eventSnap = await getDocs(
    query(collection(db, 'consumption_events'), where('bottle_id', '==', bottleId), limit(1))
  );
  const [eventDoc] = eventSnap.docs;
  if (!eventDoc) return { event: null, tastingNote: null };

  const event = { id: eventDoc.id, ...(eventDoc.data() as Omit<ConsumptionEvent, 'id'>) };

  let tastingNote: TastingNote | null = null;
  if (event.tasting_note_id) {
    const noteSnap = await getDoc(doc(db, 'tasting_notes', event.tasting_note_id));
    if (noteSnap.exists()) {
      tastingNote = { id: noteSnap.id, ...(noteSnap.data() as Omit<TastingNote, 'id'>) };
    }
  }

  return { event, tastingNote };
}

// ---------------------------------------------------------------------------
// Upsert — lets the owner add or edit a tasting note after the fact.
// Covers two real cases: (1) bottles opened via OpenBottleModal where the
// note is edited/expanded later, and (2) bottles that entered as already
// `consumed` (e.g. bulk MWC import) and never had a ConsumptionEvent at all.
// ---------------------------------------------------------------------------

export interface UpsertTastingNoteInput {
  bottleId: string;
  wineId?: string;
  /** Pass the existing record (from getConsumptionForBottle) so we know whether to create or update. */
  existing: ConsumptionRecord;
  location?: string;
  note?: string;
  personalScore100pt?: number;
}

export async function upsertTastingNote(input: UpsertTastingNoteInput): Promise<void> {
  const location = input.location?.trim() || undefined;
  const note = input.note?.trim() || undefined;
  const { existing } = input;

  const batch = writeBatch(db);
  let tastingNoteId = existing.tastingNote?.id;

  if (existing.tastingNote) {
    // Update in place — keep the original date, just refresh the content.
    batch.update(doc(db, 'tasting_notes', existing.tastingNote.id), stripUndefined({
      free_text: note,
      personal_score_100pt: input.personalScore100pt,
    }));
  } else if (note || input.personalScore100pt != null) {
    const noteRef = doc(collection(db, 'tasting_notes'));
    tastingNoteId = noteRef.id;
    batch.set(noteRef, stripUndefined({
      bottle_id: input.bottleId,
      wine_id: input.wineId,
      date: serverTimestamp(),
      mode: 'free_text',
      free_text: note,
      personal_score_100pt: input.personalScore100pt,
    }));
  }

  if (existing.event) {
    batch.update(doc(db, 'consumption_events', existing.event.id), stripUndefined({
      location,
      tasting_note_id: tastingNoteId,
    }));
  } else if (location || tastingNoteId) {
    const eventRef = doc(collection(db, 'consumption_events'));
    batch.set(eventRef, stripUndefined({
      bottle_id: input.bottleId,
      date: serverTimestamp(),
      location,
      tasting_note_id: tastingNoteId,
    }));
  }

  await batch.commit();
}
